import React, { useState } from "react";
import { Alert, Box, Button, Divider, FormControlLabel, Grid, Popover, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DataLayout, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { useEditArtefacts, useFetchArtefactReviewers } from "../mutation";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { ArtefactDetails as ArtefactDetailsType, ArtefactReviewer } from "../types";

const statusColorMap: Record<string, "red" | "green" | "amber" | "blue" | "default" | "violet" | "grey"> = {
    requiring_review: 'grey',
    in_review: 'blue',
    accepted: 'green',
    accept_with_comments: 'green',
    rejected: 'red',
    reject_with_comments: 'red',
};

export const ArtefactAcceptTab = ({ artefactId, artefactDetails, refetch, refetchArtefacts, onEditArtefact }: {
    artefactId: string | null;
    artefactDetails: ArtefactDetailsType | undefined;
    refetch: () => void;
    refetchArtefacts: () => void;
    onEditArtefact: () => void;
}) => {
    const { isAllowedEditArtefact, isAllowedApproveArtefact, isAllowedRejectArtefact, isAllowedReviewArtefact } = useCapabilities();

    const editArtefacts = useEditArtefacts();

    const { data: reviewersData, isLoading: isLoadingReviewers, isError: isReviewersError } = useFetchArtefactReviewers(artefactId);
    const reviewers: ArtefactReviewer[] = Array.isArray(reviewersData) ? reviewersData : [];
    // Fail closed: an unknown reviewer list (loading/error) must not unlock Accept.
    // A successfully fetched empty list is explicitly treated as "no pending reviews".
    const allReviewersComplete = !isLoadingReviewers && !isReviewersError && Array.isArray(reviewersData)
        && reviewers.every((reviewer) => reviewer.status !== 'awaiting_review');

    const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedStatusAction, setSelectedStatusAction] = useState<string>('');
    const [initialStatusAction, setInitialStatusAction] = useState<string>('');
    const [statusComment, setStatusComment] = useState<string>('');

    const statusActions = [
        { key: 'in_review', label: 'Mark as In Review', requiresComment: false, allowed: isAllowedReviewArtefact, disabled: false },
        { key: 'accepted', label: 'Accept', requiresComment: false, allowed: isAllowedApproveArtefact, disabled: !allReviewersComplete },
        { key: 'accept_with_comments', label: 'Accept with Comments', requiresComment: true, allowed: isAllowedApproveArtefact, disabled: !allReviewersComplete },
        { key: 'rejected', label: 'Reject', requiresComment: false, allowed: isAllowedRejectArtefact, disabled: false },
        { key: 'reject_with_comments', label: 'Reject with Comments', requiresComment: true, allowed: isAllowedRejectArtefact, disabled: false },
    ].filter((action) => action.allowed);

    const selectedActionDetails = statusActions.find((action) => action.key === selectedStatusAction);
    const isCommentInvalid = !!selectedActionDetails?.requiresComment && !statusComment.trim();
    const isStatusUnchanged = selectedStatusAction === initialStatusAction;
    const hasPendingReviews = statusActions.some((action) => action.disabled);

    const handleOpenStatusMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        const currentAction = statusActions.find((action) => action.key === artefactDetails?.status);
        setStatusAnchorEl(event.currentTarget);
        setSelectedStatusAction(currentAction?.key || '');
        setInitialStatusAction(currentAction?.key || '');
        setStatusComment('');
    };

    const handleCloseStatusMenu = () => {
        setStatusAnchorEl(null);
        setSelectedStatusAction('');
        setInitialStatusAction('');
        setStatusComment('');
    };

    const onApproved = async (payload: {
        status: string,
        comments?: { comment_topic: string, object_id: string, object_type: string, comment_text: string },
    }) => {
        if (artefactId) {
            await editArtefacts.mutateAsync({ payload: payload, artefactId });
            refetch();
            refetchArtefacts();
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedActionDetails) return;

        await onApproved({
            status: selectedActionDetails.key,
            ...(selectedActionDetails.requiresComment && {
                comments: {
                    comment_topic: "PROJECT_ARTEFACT",
                    object_id: String(artefactDetails?.id),
                    object_type: "ARTEFACT_COMMENT_ADDED",
                    comment_text: statusComment,
                },
            }),
        });
        handleCloseStatusMenu();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            <Box display={'flex'} flex={1} flexDirection={'column'} gap={2} sx={{ overflowY: 'auto' }}>
                <DataLayout title="Artefact Details" bgVariant="filled">
                    <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>
                        {
                            [
                                // { label: 'Artefact ID', value: artefactDetails.artefact_ref },
                                { label: 'Artefact Title', value: artefactDetails?.title },
                                { label: 'Artefact Description', value: artefactDetails?.description },
                                { label: 'Category', value: artefactDetails?.category },
                                { label: 'Project Title', value: artefactDetails?.project_title },
                                { label: 'Stage gate', value: artefactDetails?.stage_gate_name },
                                { label: 'Assigned to', value: artefactDetails?.user_name },
                                { label: 'Date Submitted', value: formatDate(String(artefactDetails?.created_at)) },
                                { label: 'Current Version', value: 'v1' },
                                { label: 'Current Status', value: artefactDetails?.status_display_text || 'N/A', isStatus: true },
                            ].map((value) => (
                                <Grid container key={value.label}>
                                    <Grid size={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            {value.label}
                                        </Typography>
                                    </Grid>
                                    <Grid size={6}>
                                        {
                                            value.isStatus ? (
                                                <StatusChip
                                                    label={value.value}
                                                    color={statusColorMap[String(artefactDetails?.status)] || 'default'}
                                                    variant="filled"
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.primary">
                                                    {value.value}
                                                </Typography>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            ))
                        }
                    </Box>
                </DataLayout>
                <Alert severity="error" sx={{ my: 2 }}>
                    <Typography variant="body1">
                        {
                            artefactDetails?.is_expired ?
                                'Artefact Expired' : 'Artefact Not Expired'
                        }
                    </Typography>
                    <Typography variant="subtitle2">
                        {
                            artefactDetails?.is_expired ?
                                `Expired on ${artefactDetails?.expiry_date ? formatDate(artefactDetails?.expiry_date) : 'N/A'}` :
                                `Expire on ${artefactDetails?.expiry_date ? formatDate(artefactDetails?.expiry_date) : 'N/A'}`
                        }
                    </Typography>
                </Alert>
            </Box>
            <Box display={'flex'} flexShrink={0} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                {
                    isAllowedEditArtefact && (
                        <Button variant="text" onClick={onEditArtefact}>Edit Artefact</Button>
                    )
                }
                {
                    statusActions.length > 0 && (
                        <>
                            <Button
                                variant="contained"
                                endIcon={<ExpandMoreIcon />}
                                onClick={handleOpenStatusMenu}
                            >
                                Update Status
                            </Button>
                            <Popover
                                open={Boolean(statusAnchorEl)}
                                anchorEl={statusAnchorEl}
                                onClose={handleCloseStatusMenu}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            >
                                <Box p={2} width={320} display={'flex'} flexDirection={'column'} gap={1.5}>
                                    <Typography variant="subtitle2">Update Status</Typography>
                                    <RadioGroup
                                        value={selectedStatusAction}
                                        onChange={(_event, value) => { setSelectedStatusAction(value); setStatusComment(''); }}
                                    >
                                        {
                                            statusActions.map((action) => (
                                                <FormControlLabel
                                                    key={action.key}
                                                    value={action.key}
                                                    control={<Radio size="small" />}
                                                    disabled={action.disabled}
                                                    label={action.key === initialStatusAction ? `${action.label} (Current)` : action.label}
                                                />
                                            ))
                                        }
                                    </RadioGroup>
                                    {
                                        hasPendingReviews && (
                                            <Typography variant="caption" color="text.secondary">
                                                All reviewers must complete their review before this artefact can be accepted.
                                            </Typography>
                                        )
                                    }
                                    {
                                        selectedActionDetails?.requiresComment && (
                                            <TextField
                                                label="Comment"
                                                placeholder="Add a comment explaining your decision"
                                                value={statusComment}
                                                onChange={(event) => setStatusComment(event.target.value)}
                                                multiline
                                                minRows={3}
                                                size="small"
                                                fullWidth
                                                required
                                            />
                                        )
                                    }
                                    <Divider />
                                    <Box display={'flex'} justifyContent={'flex-end'} gap={1}>
                                        <Button variant="text" onClick={handleCloseStatusMenu} disabled={editArtefacts.isPending}>Cancel</Button>
                                        <Button
                                            variant="contained"
                                            disabled={!selectedStatusAction || isCommentInvalid || isStatusUnchanged || !!selectedActionDetails?.disabled || editArtefacts.isPending}
                                            loading={editArtefacts.isPending}
                                            onClick={handleUpdateStatus}
                                        >
                                            Update
                                        </Button>
                                    </Box>
                                </Box>
                            </Popover>
                        </>
                    )
                }
            </Box>
        </Box>
    );
};
